/* ═══════════════════════════════════════════════════════════════════
   bookPdf.js — zero-cost client-side PDF export.

   Layout: A4 portrait, single-column vertical flow. One book-page per
   PDF sheet so each entry feels like a distinct page in a professional
   chronicle.  Each page break is implicit (jsPDF.addPage()).

   Performance optimisations for mobile:
     1. Request resized images (max 800px) via /api/image-proxy?w=800 —
        cuts download from ~3-5 MB to ~50-150 KB per image.
     2. Cache fetched data URLs by URL within the export run so the same
        image isn't fetched twice.
     3. html2canvas runs sequentially with a small scale on mobile so the
        device doesn't run out of memory.
     4. Multi-method file save: jsPDF.save() (handles correct
        application/pdf MIME) with a blob+anchor fallback for WebViews.
   ═══════════════════════════════════════════════════════════════════ */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BACKEND_URL } from './api';

const isMobile = () =>
  typeof navigator !== 'undefined' &&
  /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');

/** Cache so the same image isn't fetched twice during an export run. */
const dataUrlCache = new Map();

/** Down-scale an existing data URL via canvas to keep PDF sizes low. */
async function compressDataUrl(dataUrl, maxWidth = 900, quality = 0.82) {
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxWidth / img.naturalWidth);
        const w = Math.round(img.naturalWidth * ratio);
        const h = Math.round(img.naturalHeight * ratio);
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL('image/jpeg', quality));
      } catch (_e) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Fetch a remote URL as a compressed data URL. CORS is bypassed via our
 *  /api/image-proxy which whitelists Firebase Storage etc. We pass a
 *  width hint so the proxy returns a small resized JPEG instead of the
 *  multi-megabyte original. */
async function urlToDataUrl(url) {
  if (!url) return null;
  if (dataUrlCache.has(url)) return dataUrlCache.get(url);
  if (url.startsWith('data:')) {
    const c = await compressDataUrl(url);
    dataUrlCache.set(url, c);
    return c;
  }

  const fetchAsDataUrl = async (target) => {
    const resp = await fetch(target, { credentials: 'omit', cache: 'force-cache' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  };

  try {
    const sameOrigin = url.startsWith(window.location.origin) || url.startsWith('/');
    const raw = sameOrigin
      ? await fetchAsDataUrl(url)
      : await fetchAsDataUrl(`${BACKEND_URL}/api/image-proxy?w=800&url=${encodeURIComponent(url)}`);
    const compressed = await compressDataUrl(raw);
    dataUrlCache.set(url, compressed);
    return compressed;
  } catch (err) {
    console.warn('[bookPdf] Could not inline image', url, err?.message || err);
    dataUrlCache.set(url, null);
    return null;
  }
}

/** Replace every <img>.src inside `root` with an inline data URL. */
async function inlineAllImages(root) {
  const imgs = Array.from(root.querySelectorAll('img'));
  const uniqueSrcs = Array.from(new Set(
    imgs.map((i) => i.getAttribute('src') || '').filter(Boolean)
  ));
  console.info(`[bookPdf] Inlining ${imgs.length} <img> tags from ${uniqueSrcs.length} unique URLs…`);

  const concurrency = isMobile() ? 3 : 6;
  for (let i = 0; i < uniqueSrcs.length; i += concurrency) {
    const batch = uniqueSrcs.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.map((u) => urlToDataUrl(u)));
  }

  await Promise.all(imgs.map(async (img) => {
    const original = img.getAttribute('src') || '';
    if (!original || original.startsWith('data:')) return;
    const dataUrl = dataUrlCache.get(original);
    if (!dataUrl) return;
    await new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      img.src = dataUrl;
      setTimeout(done, 600);
    });
  }));
  console.info('[bookPdf] Image inlining complete');
}

/** Bulletproof PDF save — tries jsPDF.save() (cross-browser handler with
 *  correct application/pdf MIME) and falls back to an explicit blob+anchor
 *  for WebView contexts that don't fire jsPDF's save method. */
function savePdf(pdf, fileName) {
  try {
    pdf.save(`${fileName}.pdf`);
    return;
  } catch (e) {
    console.warn('[bookPdf] pdf.save() failed, falling back to blob anchor', e);
  }
  try {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 2000);
  } catch (e2) {
    console.error('[bookPdf] Blob anchor download failed', e2);
    // Last resort — open in a new tab so the user can manually save.
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
}

/**
 * Render an array of React-rendered DOM nodes into an A4 portrait PDF
 * (one book-page per sheet) and trigger a download.
 */
export async function exportBookAsFlipbookPdf(root, fileName = 'imperium_book') {
  if (!root) throw new Error('No root element provided');
  await new Promise((r) => setTimeout(r, 500));

  const pageNodes = Array.from(root.querySelectorAll('.book-print-page'));
  console.info(`[bookPdf] Starting export — ${pageNodes.length} pages`);
  if (pageNodes.length === 0) throw new Error('No pages found to export');

  await inlineAllImages(root);

  const scale = isMobile() ? 1.2 : 1.8;
  const captures = [];
  for (let i = 0; i < pageNodes.length; i++) {
    const node = pageNodes[i];
    try {
      // eslint-disable-next-line no-await-in-loop
      const cv = await html2canvas(node, {
        backgroundColor: null,
        scale,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 1500,
        windowWidth: node.offsetWidth,
        windowHeight: node.offsetHeight,
        onclone: (doc) => {
          /* Fix CSS that html2canvas can't render reliably. */
          doc.querySelectorAll('*').forEach((el) => {
            const style = el.style;
            if (!style) return;
            const wbc = style.webkitBackgroundClip || style.backgroundClip;
            if (wbc === 'text' || (el.style.cssText || '').includes('background-clip: text')) {
              style.background = 'none';
              style.webkitBackgroundClip = 'border-box';
              style.backgroundClip = 'border-box';
              style.webkitTextFillColor = '#D4AF37';
              style.color = '#D4AF37';
              style.textShadow = '0 1px 0 rgba(0,0,0,0.35)';
            }
          });
          doc.querySelectorAll('img').forEach((img) => {
            const s = img.style;
            if (s && (s.filter || '').includes('blur')) {
              img.style.display = 'none';
              return;
            }
            if (s) {
              s.position = 'static';
              s.inset = 'auto';
              s.left = ''; s.right = ''; s.top = ''; s.bottom = '';
              if ((s.objectFit || '') === 'cover' || (s.objectFit || '') === 'contain') {
                s.objectFit = '';
              }
              s.width = '100%';
              s.height = '100%';
              s.display = 'block';
            }
          });
          doc.querySelectorAll('[style*="aspect-ratio"], [style*="aspectRatio"], [style*="aspect"]').forEach((el) => {
            const ar = el.style.aspectRatio || '';
            const m = ar.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
            const w = el.getBoundingClientRect().width || 320;
            if (m) {
              const ratio = parseFloat(m[1]) / parseFloat(m[2]);
              el.style.height = `${Math.round(w / ratio)}px`;
            }
            el.style.aspectRatio = '';
          });
        },
      });
      captures.push(cv);
      console.info(`[bookPdf] Captured page ${i + 1}/${pageNodes.length}`);
    } catch (err) {
      console.error(`[bookPdf] Capture failed on page ${i + 1}`, err);
      const blank = document.createElement('canvas');
      blank.width = 800; blank.height = 1440;
      const ctx = blank.getContext('2d');
      ctx.fillStyle = '#f0dfa8'; ctx.fillRect(0, 0, 800, 1440);
      captures.push(blank);
    }
  }

  /* ──────────────────────────  PDF COMPOSITION  ──────────────────────────
     A4 PORTRAIT, one captured book-page per sheet.  Each page fills the
     whole sheet edge-to-edge (10mm safe margin on every side) so the PDF
     reads like a clean vertical-scrolling chronicle.  jsPDF.addPage()
     automatically inserts the page-break the user asked for. */
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210, pageH = 297;
  const margin = 8;
  const contentW = pageW - margin * 2;
  // Preserve the captured page's natural aspect ratio (100:180 portrait).
  const naturalRatio = 180 / 100; // height/width
  let contentH = contentW * naturalRatio;
  if (contentH > pageH - margin * 2) contentH = pageH - margin * 2;
  const finalW = contentH / naturalRatio;
  const offsetX = (pageW - finalW) / 2;
  const offsetY = (pageH - contentH) / 2;

  for (let i = 0; i < captures.length; i++) {
    if (i > 0) pdf.addPage();
    // Dark ink fill behind the page so any rounded corner / margin
    // bleeds into a leather-bound chronicle look.
    pdf.setFillColor(26, 18, 8);
    pdf.rect(0, 0, pageW, pageH, 'F');
    const cv = captures[i];
    pdf.addImage(
      cv.toDataURL('image/jpeg', 0.9),
      'JPEG', offsetX, offsetY, finalW, contentH, undefined, 'FAST',
    );
  }

  console.info('[bookPdf] Composing PDF & triggering download');
  savePdf(pdf, fileName);
  console.info('[bookPdf] Done');
}
